// SPDX-License-Identifier: AGPL-3.0-or-later
/**
 * Splits findings into pre-submit (client-runnable) and post-submit
 * (server-only) buckets.
 *
 * Implementation note: the switch is intentionally exhaustive over
 * {@link FindingCode}. When a new code is added to the type union,
 * TypeScript will fail this file with a "Type 'X' is not assignable to
 * type 'never'" error on the `_exhaustive` line — forcing the author to
 * decide which bucket the new code belongs to instead of silently
 * defaulting to one. This is the drift-prevention contract the gap
 * analysis flagged.
 *
 * Contradiction findings respect a per-rule `visibility` flag carried in
 * `Finding.context.visibility` (set by the cross-field pass). Rules
 * marked `visibility: 'patient'` move to the form-level bucket so the
 * patient sees them inline; anything else stays clinical. Default is
 * clinician-only — see `resolveVisibility` in cross-field.ts.
 */
export function partitionFindings(findings) {
    const out = { structural: [], formLevel: [], clinical: [] };
    for (const f of findings) {
        const bucket = bucketFor(f);
        if (bucket === 'structural' && f.linkId === undefined) {
            out.formLevel.push(f);
        }
        else {
            out[bucket].push(f);
        }
    }
    return out;
}
// Structural codes run client-side in the field pass and are safe to
// surface inline before Submit. phone-invalid / phone-unparseable come
// from libphonenumber-js (in-apm); dob-future / dob-implausible from
// the DOB sanity pass (in-3uo).
// Clinical codes are server-only — either they encode rules we don't
// want to expose, or they need a network round-trip the client can't
// do. email-no-mx / email-disposable (in-2ni) and the phone-twilio-*
// pair (in-apm) are async network validators.
function bucketFor(finding) {
    switch (finding.code) {
        case 'required':
        case 'type':
        case 'regex':
        case 'min':
        case 'max':
        case 'incomplete':
        case 'phone-invalid':
        case 'phone-unparseable':
        case 'dob-future':
        case 'dob-implausible':
            return 'structural';
        case 'contradiction':
            // Per-rule opt-in: contradictions tagged `visibility: 'patient'`
            // flow to the form-level bucket so they surface as a banner near
            // Submit. Default stays clinical for safety.
            return contradictionVisibility(finding) === 'patient' ? 'formLevel' : 'clinical';
        case 'eligibility':
        case 'stale':
        case 'async':
        case 'email-no-mx':
        case 'email-disposable':
        case 'phone-twilio-invalid':
        case 'phone-twilio-line-type':
            return 'clinical';
        default: {
            const _exhaustive = finding.code;
            return _exhaustive;
        }
    }
}
function contradictionVisibility(finding) {
    const v = finding.context?.visibility;
    return v === 'patient' ? 'patient' : 'clinician-only';
}
//# sourceMappingURL=partition.js.map